import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';
import { useCampaignReportDownload, useLiveCampaigns } from '../api';
import { buildCampaignColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import {
  CampaignFilters,
  applyCampaignFilters,
  rowsHaveRetries,
} from '../components/CampaignFilters';
import { CampaignSummaryBar } from '../components/CampaignSummaryBar';
import type { CampaignRow } from '../types';

export function VoiceCampaignLivePage() {
  const query = useLiveCampaigns();
  const allRows = query.data ?? [];
  const reportDownload = useCampaignReportDownload();

  /* ---- Filters (Status + Campaign Type) ---- */
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const rows = useMemo(
    () => applyCampaignFilters(allRows, selectedStatuses, selectedTemplates),
    [allRows, selectedStatuses, selectedTemplates],
  );

  /*
   * Auto-show retry-related columns + summary pills when any visible row
   * actually carries retry data. Matches legacy `hasRetries` semantics —
   * users who don't use the retry module never see noise columns.
   */
  const hasRetries = rowsHaveRetries(rows);
  const baseTotal = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.numbersUploaded ?? 0), 0),
    [rows],
  );

  const columns = useMemo(
    () =>
      buildCampaignColumns({
        onDownloadReport: reportDownload.download,
        baseTotal,
      }),
    [reportDownload.download, baseTotal],
  );

  const visibility = {
    ...DEFAULT_VISIBLE_COLUMNS,
    retryCount: hasRetries,
    retries: hasRetries,
  };

  return (
    <FillPage>
      <PageHeader
        title="Live Campaigns"
        description="Currently running and recently scheduled campaigns. Refreshes every 30 seconds."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw
                className={cn('h-4 w-4', query.isFetching && 'animate-spin')}
              />
            </Button>
            <Button asChild>
              <Link to="/add-voice-campaign">
                <Plus className="h-4 w-4" />
                Compose campaign
              </Link>
            </Button>
          </div>
        }
      />

      <DataTable<CampaignRow, unknown>
        tableId="campaigns:voice:live:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search campaigns…"
        emptyTitle="No live campaigns"
        emptyDescription="Compose a campaign to start dialing."
        getRowId={(row) => String(row.campaignId)}
        initialColumnPinning={{ right: ['actions'] }}
        initialColumnVisibility={visibility}
        exportFilename="voice-campaigns-live.csv"
        pageSize={10}
        toolbar={
          <CampaignFilters
            selectedStatuses={selectedStatuses}
            selectedTemplates={selectedTemplates}
            onStatusChange={setSelectedStatuses}
            onTemplateChange={setSelectedTemplates}
          />
        }
        beforeTable={
          <CampaignSummaryBar rows={rows} showRetry={hasRetries} />
        }
      />
    </FillPage>
  );
}
